import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import type { OrgAdminOrganizationRef } from '@dermalink/shared';
import {
  AuthContext,
  UserNameContext,
  IsDoctorContext,
  IsOperatorContext,
  OrgAdminOfContext,
  type AuthUser,
  getAuthToken,
  resolveDisplayName,
  resolveDisplayNamePreferApiProfile,
  displayNameFromProfileFields,
  resolveUserRole,
  resolveUserRoles,
  resolveIsOperator,
  useIsDoctor,
} from './auth.ts';
import { ROUTES } from './routes.ts';
import { rememberReturnTo, consumeReturnTo } from '@/utils';
import { getConfig } from './config.ts';
import { MaintenancePage } from './pages/MaintenancePage.tsx';
import { Login } from './pages/Login.tsx';
import { SuspendedAccount } from './pages/SuspendedAccount.tsx';
import { PatientDashboard } from './pages/patient/PatientDashboard.tsx';
import { Payment } from './pages/patient/Payment.tsx';
import { Callback } from './pages/Callback.tsx';
import { Profile } from './pages/Profile.tsx';
import { Intake } from './pages/patient/Intake.tsx';
import { PracticePicker } from './pages/patient/PracticePicker.tsx';
import { DoctorsOverview } from './pages/patient/DoctorsOverview.tsx';
import { ConversationThread } from './pages/ConversationThread.tsx';
import { DoctorDashboard } from './pages/doctor/DoctorDashboard.tsx';
import { DoctorConsultations } from './pages/doctor/DoctorConsultations.tsx';
import { Verification } from './pages/doctor/Verification.tsx';
import { Payments } from './pages/doctor/Payments.tsx';
import { Statistics } from './pages/doctor/Statistics.tsx';
import { DoctorProfile } from './pages/doctor/DoctorProfile.tsx';
import { DoctorSupportTickets } from './pages/doctor/DoctorSupportTickets.tsx';
import { PlatformInfo } from './pages/patient/PlatformInfo.tsx';
import { MedicalDisclaimer } from './pages/patient/MedicalDisclaimer.tsx';
import { UserAgreement } from './pages/patient/UserAgreement.tsx';
import { PrivacyPolicy } from './pages/patient/PrivacyPolicy.tsx';
import { OperatorDashboard } from './pages/operator/OperatorDashboard.tsx';
import { OperatorAnalytics } from './pages/operator/OperatorAnalytics.tsx';
import { OperatorOperations } from './pages/operator/OperatorOperations.tsx';
import { OperatorAuditLog } from './pages/operator/OperatorAuditLog.tsx';
import { OperatorDoctors } from './pages/operator/OperatorDoctors.tsx';
import { OperatorOrganizations } from './pages/operator/OperatorOrganizations.tsx';
import { OperatorPatients } from './pages/operator/OperatorPatients.tsx';
import { OperatorHygiene } from './pages/operator/OperatorHygiene.tsx';
import { OperatorSupportTickets } from './pages/operator/OperatorSupportTickets.tsx';
import { PracticeAdmin } from './pages/orgadmin/PracticeAdmin.tsx';
import { PracticeReport } from './pages/orgadmin/PracticeReport.tsx';
import { PracticeSettings } from './pages/orgadmin/PracticeSettings.tsx';
import { PracticePayments, PracticePaymentsMollieCallback } from './pages/orgadmin/PracticePayments.tsx';

interface RouteGuardProps {
  children: React.ReactNode;
  /**
   * When true, also checks profile completeness (redirects to /profile if
   * missing or consent not given) and resolves the doctor role context.
   * Use for all app routes except the profile setup page itself.
   */
  requireProfile?: boolean;
}

/**
 * Unified route guard.
 *
 * - Without `requireProfile`: verifies Cognito session only (used for /profile
 *   so the user can complete setup without being bounced in a redirect loop).
 * - With `requireProfile`: additionally checks that the profile is complete and
 *   consent has been given, then resolves the doctor role for IsDoctorContext.
 *
 * The role check always fetches fresh from the server so that changes to
 * doctor_profiles are reflected immediately without any client-side cache.
 */
function RouteGuard({ children, requireProfile = false }: RouteGuardProps) {
  const [user, setUser]             = useState<AuthUser | null>(null);
  const [userName, setUserName]     = useState('');
  const [isDoctor, setIsDoctor]     = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [orgAdminOf, setOrgAdminOf] = useState<OrgAdminOrganizationRef[]>([]);
  const [ready, setReady]           = useState(false);
  const navigate = useNavigate();
  const mounted  = useRef(true);

  useEffect(() => {
    mounted.current = true;

    async function check() {
      // Captured up front: by the time an await below resolves, a concurrent check
      // (StrictMode runs effects twice) may already have navigated to /login.
      const requestedPath = window.location.pathname + window.location.search;

      // ── Step 1: verify Cognito session ────────────────────────
      let u: AuthUser;
      try {
        u = await getCurrentUser();
      } catch {
        if (!mounted.current) return;
        rememberReturnTo(requestedPath);
        navigate(ROUTES.login, { replace: true });
        return;
      }
      if (!mounted.current) return;
      setUser(u);

      // ── Step 2: auth-only guard — done ────────────────────────
      if (!requireProfile) {
        const name = await resolveDisplayNamePreferApiProfile(getConfig().api_url);
        if (!mounted.current) return;
        setUserName(name);
        setReady(true);
        return;
      }

      // ── Step 3: profile completeness check ────────────────────
      const token = await getAuthToken();
      if (!token) {
        if (!mounted.current) return;
        navigate(ROUTES.login, { replace: true });
        return;
      }

      let nameFromProfile: string | null = null;
      try {
        const res = await fetch(`${getConfig().api_url}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!mounted.current) return;

        if (res.status === 401) {
          navigate(ROUTES.login, { replace: true });
          return;
        }
        if (res.status === 404) {
          rememberReturnTo(requestedPath);
          navigate(ROUTES.profile, { replace: true, state: { reason: 'missing_info' } });
          return;
        }
        if (res.status === 409) {
          rememberReturnTo(requestedPath);
          const body = await res.json().catch(() => null) as { email?: string } | null;
          navigate(ROUTES.profile, { replace: true, state: { reason: 'email_exists', email: body?.email ?? null } });
          return;
        }
        if (res.status === 403) {
          const body = await res.json().catch(() => null) as { error?: string; reason?: string | null } | null;
          if (body?.error === 'account_suspended') {
            navigate(ROUTES.suspended, { replace: true, state: { reason: body.reason ?? null } });
            return;
          }
          // Any other 403 → fail open (don't block the app)
        }
        if (res.ok) {
          const p = (await res.json()) as {
            consent?: boolean;
            first_name?: string | null;
            last_name?: string | null;
          };
          if (!p.consent) {
            rememberReturnTo(requestedPath);
            navigate(ROUTES.profile, { replace: true, state: { reason: 'no_consent' } });
            return;
          }
          nameFromProfile = displayNameFromProfileFields(p.first_name, p.last_name);
        }
        // Unexpected API error → fail open (don't block the app)
      } catch { /* fail open */ }

      // ── Step 4: resolve doctor role + operator flag ────────────
      if (!mounted.current) return;
      const [roles, operator] = await Promise.all([resolveUserRoles(), resolveIsOperator()]);
      if (!mounted.current) return;
      setIsDoctor(roles.role === 'doctor');
      setIsOperator(operator);
      setOrgAdminOf(roles.orgAdminOf);

      const name = nameFromProfile ?? (await resolveDisplayName());
      if (!mounted.current) return;
      setUserName(name);
      setReady(true);
    }

    check();
    return () => { mounted.current = false; };
  }, [navigate, requireProfile]);

  if (!ready) return null;

  return (
    <AuthContext.Provider value={user}>
      <UserNameContext.Provider value={userName}>
        <IsDoctorContext.Provider value={isDoctor}>
          <IsOperatorContext.Provider value={isOperator}>
            <OrgAdminOfContext.Provider value={orgAdminOf}>
              {children}
            </OrgAdminOfContext.Provider>
          </IsOperatorContext.Provider>
        </IsDoctorContext.Provider>
      </UserNameContext.Provider>
    </AuthContext.Provider>
  );
}

/**
 * Redirects `/` to the user's role-based home (/patient or /doctor/dashboard).
 * Falls back to /login if unauthenticated or if the role fetch fails.
 */
function HomeRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    async function redirect() {
      try {
        const token = await getAuthToken();
        if (!token) { navigate(ROUTES.login, { replace: true }); return; }
        const returnTo = consumeReturnTo();
        if (returnTo) { navigate(returnTo, { replace: true }); return; }
        const role = await resolveUserRole();
        navigate(role === 'doctor' ? ROUTES.doctorConsultations : ROUTES.practices, { replace: true });
      } catch {
        navigate(ROUTES.login, { replace: true });
      }
    }
    redirect();
  }, [navigate]);

  return null;
}

/** Doctors have no patient pages — send them to their own consultations list instead. */
function PatientRoute({ children }: { children: React.ReactNode }) {
  const isDoctor = useIsDoctor();
  if (isDoctor) return <Navigate to={ROUTES.doctorConsultations} replace />;
  return children;
}

export default function App() {
  if (getConfig().maintenance_mode) {
    return <MaintenancePage />;
  }

  return (
    <Routes>
      <Route path="/"                      element={<HomeRedirect />} />
      <Route path={ROUTES.practices}       element={<RouteGuard requireProfile><PatientRoute><PracticePicker /></PatientRoute></RouteGuard>} />
      <Route path={ROUTES.practice}        element={<RouteGuard requireProfile><PatientRoute><PatientDashboard /></PatientRoute></RouteGuard>} />
      <Route path={ROUTES.practiceIntake}  element={<RouteGuard requireProfile><Intake /></RouteGuard>} />
      <Route path={ROUTES.practiceConversation} element={<RouteGuard requireProfile><ConversationThread /></RouteGuard>} />
      <Route path={ROUTES.patient}         element={<Navigate to={ROUTES.practices} replace />} />
      <Route path={ROUTES.intake}          element={<Navigate to={ROUTES.practices} replace />} />
      <Route path={ROUTES.profile}         element={<RouteGuard><Profile /></RouteGuard>} />
      <Route path={ROUTES.doctors}         element={<RouteGuard requireProfile><DoctorsOverview /></RouteGuard>} />
      <Route path={ROUTES.platformInfo}     element={<PlatformInfo />} />
      <Route path={ROUTES.medicalDisclaimer} element={<MedicalDisclaimer />} />
      <Route path={ROUTES.userAgreement}    element={<UserAgreement />} />
      <Route path={ROUTES.privacyPolicy}    element={<PrivacyPolicy />} />
      <Route path={ROUTES.conversationPay} element={<RouteGuard requireProfile><Payment /></RouteGuard>} />
      <Route path={ROUTES.conversation}    element={<RouteGuard requireProfile><ConversationThread /></RouteGuard>} />
      <Route path={ROUTES.doctorDashboard} element={<RouteGuard requireProfile><DoctorDashboard /></RouteGuard>}>
        <Route index element={<Navigate to={ROUTES.doctorConsultations} replace />} />
        <Route path="consultations" element={<DoctorConsultations />} />
        <Route path="verification"  element={<Verification />} />
        <Route path="payments"      element={<Payments />} />
        <Route path="statistics"    element={<Statistics />} />
        <Route path="profile"       element={<DoctorProfile />} />
        <Route path="support"       element={<DoctorSupportTickets />} />
      </Route>
      <Route path={ROUTES.practiceAdmin}    element={<RouteGuard requireProfile><PracticeAdmin /></RouteGuard>}>
        <Route index element={<Navigate to={ROUTES.practiceAdminReport} replace />} />
        <Route path="overzicht" element={<PracticeReport />} />
        <Route path="huisstijl" element={<PracticeSettings />} />
        <Route path="betalingen" element={<PracticePayments />} />
        <Route path="betalingen/mollie" element={<PracticePaymentsMollieCallback />} />
      </Route>
      <Route path={ROUTES.practiceSettings} element={<Navigate to={ROUTES.practiceAdmin} replace />} />
      <Route path={ROUTES.operator}           element={<RouteGuard requireProfile><OperatorDashboard /></RouteGuard>}>
        <Route index element={<Navigate to={ROUTES.operatorOperations} replace />} />
        <Route path="beheer"    element={<OperatorOperations />} />
        <Route path="analytics" element={<OperatorAnalytics />} />
      </Route>
      <Route path={ROUTES.operatorAudit}      element={<RouteGuard requireProfile><OperatorAuditLog /></RouteGuard>} />
      <Route path={ROUTES.operatorDoctors}    element={<RouteGuard requireProfile><OperatorDoctors /></RouteGuard>} />
      <Route path={ROUTES.operatorOrganizations} element={<RouteGuard requireProfile><OperatorOrganizations /></RouteGuard>} />
      <Route path={ROUTES.operatorPatients}   element={<RouteGuard requireProfile><OperatorPatients /></RouteGuard>} />
      <Route path={ROUTES.operatorHygiene}    element={<RouteGuard requireProfile><OperatorHygiene /></RouteGuard>} />
      <Route path={ROUTES.operatorSupportTickets} element={<RouteGuard requireProfile><OperatorSupportTickets /></RouteGuard>} />
      <Route path={ROUTES.login}             element={<Login />} />
      <Route path={ROUTES.suspended}          element={<SuspendedAccount />} />
      <Route path={ROUTES.callback}        element={<Callback />} />
      <Route path="*"                      element={<Navigate to="/" replace />} />
    </Routes>
  );
}
